import { Box, Text } from '@mantine/core';
import DOMPurify from 'dompurify';
import classes from './RichTextContent.module.css';

export interface RichTextContentProps {
  /** HTML content to render */
  content: string | null | undefined;
  /** Text to show when content is empty */
  emptyText?: string;
  /** Additional class name */
  className?: string;
}

/**
 * RichTextContent - Safely renders HTML content from RichTextEditor
 *
 * Sanitizes HTML to prevent XSS attacks before rendering.
 * Falls back to displaying plain text if content appears to be
 * non-HTML (for backwards compatibility with existing plain text notes).
 *
 * @example
 * <RichTextContent
 *   content={notes}
 *   emptyText="No notes yet"
 * />
 */
export function RichTextContent({
  content,
  emptyText = 'No content',
  className,
}: RichTextContentProps) {
  // Handle empty content
  if (!content || content.trim() === '') {
    return (
      <Text c="dimmed" className={className}>
        {emptyText}
      </Text>
    );
  }

  // Check if content looks like HTML
  const isHtml = content.includes('<') && content.includes('>');

  if (!isHtml) {
    // Plain text content - preserve whitespace and display as-is
    return (
      <Text className={className} style={{ whiteSpace: 'pre-wrap' }}>
        {content}
      </Text>
    );
  }

  // Sanitize HTML content before rendering
  const sanitizedHtml = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'a',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

  return (
    <Box
      className={`${classes.content} ${className || ''}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}

export default RichTextContent;
